import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteNav } from '../components/SiteNav';
import { FaFacebook, FaYelp } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { SiG2, SiTripadvisor, SiGooglemaps } from "react-icons/si";
// 1. Fixed Import
import logoImage from '../assets/logo_small.png';
import { MegaFooter } from '../components/MegaFooter';
import { LiteYouTube } from '../components/LiteYouTube';
import { sendDiscordAlert } from '../utils/sendDiscordAlert';
import { trackLead } from '../utils/analytics';

type DemoState = 'selection' | 'negative' | 'positive';
type ScanState = 'idle' | 'scanning' | 'modal' | 'success';

// Google Business Search Result type
interface GoogleBusinessResult {
  placeId: string;
  name: string;
  address: string;
  rating?: number;
  totalRatings?: number;
}


export function Home() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [demoState, setDemoState] = useState<DemoState>('selection');
  const [animatedStars, setAnimatedStars] = useState(1);

  // Hero search state
  const [heroSearchTab, setHeroSearchTab] = useState<'search' | 'paste' | 'placeId'>('search');
  const [heroSearchQuery, setHeroSearchQuery] = useState('');
  const [heroSearchResults, setHeroSearchResults] = useState<GoogleBusinessResult[]>([]);
  const [isHeroSearching, setIsHeroSearching] = useState(false);
  const [showHeroResults, setShowHeroResults] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<GoogleBusinessResult | null>(null);
  const [heroPasteUrl, setHeroPasteUrl] = useState('');
  const [heroPlaceId, setHeroPlaceId] = useState('');
  const [heroEmail, setHeroEmail] = useState('');
  const heroSearchRef = useRef<HTMLDivElement>(null);

  // Lead-capture scan flow
  const [scanState, setScanState] = useState<ScanState>('idle');
  const [scanStep, setScanStep] = useState(0); // 0-2 for the 3 progress steps
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic "Visto Lift" projections based on selected business
  const projections = useMemo(() => {
    if (!selectedBusiness) return null;
    const rating = selectedBusiness.rating || 4.0;
    const totalReviews = selectedBusiness.totalRatings || 0;
    const isLarge = totalReviews >= 1000;

    // Rating boost: +0.2 to +0.4, seeded by placeId for consistency
    const seed = selectedBusiness.placeId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    const liftAmount = 0.2 + (seed % 3) * 0.1; // 0.2, 0.3, or 0.4
    const projectedRating = Math.min(5.0, rating + liftAmount);

    // Projected monthly 5-star reviews
    const projectedMonthlyReviews = isLarge ? 48 : 8 + (seed % 3); // 8, 9, or 10

    // Negative reviews intercepted: 91-98%
    const intercepted = 91 + (seed % 8);

    // NPS: 82-95
    const nps = 82 + (seed % 14);

    return { liftAmount, projectedRating, projectedMonthlyReviews, intercepted, nps };
  }, [selectedBusiness]);

  useEffect(() => {
    const starInterval = setInterval(() => {
      setAnimatedStars(prev => prev >= 5 ? 1 : prev + 1);
    }, 1000);

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).style.opacity = '1';
          (entry.target as HTMLElement).style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    document.querySelectorAll('section').forEach(section => {
      section.style.opacity = '0';
      section.style.transform = 'translateY(30px)';
      section.style.transition = 'opacity 0.8s ease-out, transform 0.8s ease-out';
      observer.observe(section);
    });

    // Finer-grained element-level reveal for .reveal elements within sections
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.reveal').forEach(el => revealObserver.observe(el));

    return () => {
      observer.disconnect();
      revealObserver.disconnect();
      clearInterval(starInterval);
    };
  }, []);

  // Google Places search for hero
  const searchGooglePlacesHero = useCallback(async (searchQuery: string) => {
    if (!searchQuery) {
      setHeroSearchResults([]);
      return;
    }

    if (typeof google === 'undefined' || !google.maps) {
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_FIREBASE_API_KEY;
      if (!apiKey) return;
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
        script.async = true;
        document.head.appendChild(script);
        script.onload = () => searchGooglePlacesHero(searchQuery);
        return;
      }
    }

    setIsHeroSearching(true);
    try {
      const { Place, AutocompleteSuggestion } = await google.maps.importLibrary('places') as any;
      const request = { input: searchQuery, includedPrimaryTypes: ['establishment'] };
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const results = await Promise.all(
        suggestions.slice(0, 5).map(async (suggestion: any) => {
          try {
            const placePrediction = suggestion.placePrediction;
            if (!placePrediction) return null;
            const place = new Place({ id: placePrediction.placeId });
            await place.fetchFields({ fields: ['id', 'displayName', 'formattedAddress', 'rating', 'userRatingCount'] });
            return {
              placeId: place.id,
              name: place.displayName || placePrediction.text?.text || 'Unknown',
              address: place.formattedAddress || placePrediction.structuredFormat?.secondaryText?.text || '',
              rating: place.rating,
              totalRatings: place.userRatingCount,
            };
          } catch {
            const placePrediction = suggestion.placePrediction;
            return {
              placeId: placePrediction?.placeId || '',
              name: placePrediction?.text?.text || 'Unknown',
              address: placePrediction?.structuredFormat?.secondaryText?.text || '',
            };
          }
        })
      );

      setHeroSearchResults(results.filter((r): r is GoogleBusinessResult => r !== null && r.placeId !== ''));
    } catch (err) {
      console.error('Hero search error:', err);
      setHeroSearchResults([]);
    } finally {
      setIsHeroSearching(false);
    }
  }, []);

  // Track whether a search_start event has been fired this session
  const hasTrackedSearchStart = useRef(false);

  // Hero search — fires after 100ms, triggered from 1 character
  useEffect(() => {
    if (!heroSearchQuery) {
      hasTrackedSearchStart.current = false;
      setHeroSearchResults([]);
      setIsHeroSearching(false);
      return;
    }

    // Fire gtag search_start event on the very first character
    if (!hasTrackedSearchStart.current) {
      hasTrackedSearchStart.current = true;
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'search_start', {
          event_category: 'engagement',
          event_label: 'hero_business_search',
        });
      }
    }

    // 100ms debounce — feels instant but avoids hammering the API on each keypress
    const timer = setTimeout(() => {
      searchGooglePlacesHero(heroSearchQuery);
    }, 100);
    return () => clearTimeout(timer);
  }, [heroSearchQuery, searchGooglePlacesHero]);

  // Close hero search results on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (heroSearchRef.current && !heroSearchRef.current.contains(event.target as Node)) {
        setShowHeroResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectBusiness = (business: GoogleBusinessResult) => {
    setSelectedBusiness(business);
    setHeroSearchQuery(business.name);
    setShowHeroResults(false);

    // Lead Sniper: silent Discord notification + top-of-funnel tracking
    sendDiscordAlert(business);
    trackLead(business.name);
  };

  const handleHeroSignup = () => {
    if (heroEmail) {
      navigate(`/signup?email=${encodeURIComponent(heroEmail)}`);
    } else {
      navigate('/signup');
    }
  };

  // Derive a fake-but-consistent review count from the placeId for the scan animation
  const fakeReviewCount = useMemo(() => {
    if (!selectedBusiness) return 14;
    const seed = selectedBusiness.placeId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return 8 + (seed % 20); // 8-27 reviews
  }, [selectedBusiness]);

  const handleGenerate = () => {
    if (!selectedBusiness) return;
    setScanState('scanning');
    setScanStep(0);

    // Step through progress messages then open modal
    const timer1 = setTimeout(() => setScanStep(1), 1000);
    const timer2 = setTimeout(() => setScanStep(2), 2100);
    const timer3 = setTimeout(() => setScanState('modal'), 3200);

    return () => { clearTimeout(timer1); clearTimeout(timer2); clearTimeout(timer3); };
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness || !leadName || !leadEmail) return;
    setIsSubmitting(true);

    try {
      await sendDiscordAlert(selectedBusiness, { name: leadName, email: leadEmail });
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'lead_capture', {
          event_category: 'conversion',
          event_label: selectedBusiness.name,
        });
      }
      setScanState('success');
    } catch {
      // Silently succeed even on error — don't block the user
      setScanState('success');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartTrial = () => {
    navigate('/signup');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const AnimatedStarRating = () => {
    return (
      <div className="flex gap-1 justify-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`material-symbols-outlined text-2xl transition-all duration-300 ${
              star <= animatedStars 
                ? 'text-yellow-400 scale-110' 
                : 'text-slate-300 dark:text-slate-600'
            }`}
            style={{ 
              fontVariationSettings: star <= animatedStars ? "'FILL' 1" : "'FILL' 0",
              filter: star <= animatedStars ? 'drop-shadow(0 0 6px rgba(250, 204, 21, 0.6))' : 'none'
            }}
          >
            star
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white font-display min-h-screen overflow-x-hidden transition-colors duration-300">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-200/40 dark:bg-emerald-900/20 rounded-full blur-[100px] mix-blend-multiply dark:mix-blend-lighten opacity-70 animate-float"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-200/40 dark:bg-blue-900/20 rounded-full blur-[100px] mix-blend-multiply dark:mix-blend-lighten opacity-70 animate-float-slow"></div>
        <div className="absolute inset-0 bg-grid z-0"></div>
      </div>

      {/* Navigation */}
      <SiteNav />


      {/* Hero Section */}
      <main className="relative z-10">
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 animate-fade-in">

              {/* LEFT: Headline + Value Prop */}
              <div className="flex-1 text-center lg:text-left max-w-xl lg:max-w-none">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6">
                  {t('hero.headline1')}{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400 dark:from-emerald-400 dark:to-teal-300">{t('hero.headline2')}</span>
                </h1>

                <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 leading-relaxed font-medium mb-8 max-w-lg mx-auto lg:mx-0">
                  {t('hero.subheadline')}
                </p>

                {/* Desktop-only buttons (hidden on mobile — shown below search card instead) */}
                <div className="hidden lg:flex flex-col sm:flex-row items-center gap-4">
                  <button onClick={handleStartTrial} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-extrabold text-lg shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all hover:-translate-y-0.5 ring-2 ring-emerald-400/30 animate-pulse-subtle inline-flex items-center gap-2">
                    {t('nav.startTrial')}
                    <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
                  </button>
                  <button onClick={() => scrollToSection('video-demo')} className="group glass-panel px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 inline-flex items-center gap-2">
                    <span className="material-symbols-outlined" aria-hidden="true">play_circle</span>
                    {t('hero.watchDemo')}
                  </button>
                </div>
              </div>

              {/* RIGHT: Interactive Business Search */}
              <div className="flex-1 w-full max-w-lg relative z-30 animate-float-slow">

                {/* ── Review → Post flow illustration ── */}
                <div className="relative z-10 mb-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 sm:p-4 shadow-md">
                  <p className="text-xs sm:text-sm font-extrabold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3 leading-tight">
                    {i18n.language.startsWith('es') ? 'Cómo funciona' : 'How it works'}
                  </p>
                  {/* items-center so arrow is vertically centred; self-start on both cards overrides stretch so aspect-square works */}
                  <div className="flex flex-row items-center gap-3">

                    {/* Google card — aspect-square wrapper + absolute fill */}
                    <div className="flex-1 min-w-0 aspect-square relative self-start">
                      <div className="absolute inset-0 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm overflow-hidden">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-7 h-7 rounded-full bg-[#4285F4] flex items-center justify-center shrink-0">
                            <span className="text-white font-bold text-xs">M</span>
                          </div>
                          <p className="text-xs font-semibold text-slate-800 leading-none truncate">Maria G.</p>
                        </div>
                        <div className="flex items-center gap-0.5 mb-1.5">
                          {[1,2,3,4,5].map(i => (
                            <svg key={i} className="w-3.5 h-3.5 text-[#FBBC04]" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                          ))}
                        </div>
                        <p className="text-xs text-slate-700 leading-snug font-medium line-clamp-4">
                          "Amazing service! Best in town, will definitely be back."
                        </p>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md shadow-emerald-500/30">
                        <span className="material-symbols-outlined text-white text-base" style={{ fontVariationSettings: "'FILL' 1" }}>arrow_forward</span>
                      </div>
                      <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">Visto</span>
                    </div>

                    {/* IG card — same aspect-square wrapper pattern */}
                    <div className="flex-1 min-w-0 aspect-square relative self-start">
                      <div className="absolute inset-0 rounded-xl overflow-hidden flex flex-col shadow-lg" style={{ background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)' }}>
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-black/25 backdrop-blur-sm shrink-0">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 p-px shrink-0">
                            <div className="w-full h-full rounded-full bg-black/60 flex items-center justify-center">
                              <span className="text-white font-bold text-[9px]">YB</span>
                            </div>
                          </div>
                          <span className="text-white text-xs font-semibold flex-1 truncate">@yourbusiness</span>
                          <span className="text-[10px] text-blue-300 font-bold shrink-0">Follow</span>
                        </div>
                        <div className="flex flex-col flex-1 items-center justify-center px-3 py-2 text-center gap-1.5">
                          <div className="flex gap-1 justify-center">
                            {[0,1,2,3,4].map(i => (
                              <svg key={i} className="w-5 h-5 text-yellow-300 animate-star-shine" style={{ animationDelay: `${i * 0.2}s` }} fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                              </svg>
                            ))}
                          </div>
                          <p className="text-white text-xs font-bold leading-snug">
                            "Amazing service! Best in town, will definitely be back."
                          </p>
                          <p className="text-white/70 text-[11px] font-medium">— Maria G.</p>
                        </div>
                        <div className="flex items-center justify-between px-2.5 py-1.5 bg-black/20 shrink-0">
                          <div className="flex items-center gap-1">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                            <span className="text-white/70 text-xs">147</span>
                          </div>
                          <span className="text-xs bg-white/20 text-white font-bold px-2 py-0.5 rounded-full">
                            {i18n.language.startsWith('es') ? 'Auto-compartir' : 'Auto-share'}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Glow behind the card */}
                <div className="absolute -inset-3 bg-gradient-to-br from-emerald-400/20 via-teal-400/10 to-emerald-400/20 rounded-3xl blur-xl animate-pulse-soft pointer-events-none"></div>

                <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-[0_20px_60px_-12px_rgba(16,185,129,0.25)] dark:shadow-[0_20px_60px_-12px_rgba(16,185,129,0.15)] border border-emerald-200/60 dark:border-emerald-800/40 overflow-visible">
                  {/* Header — social proof engine pitch */}
                  <div className="relative bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 bg-[length:200%_100%] animate-shimmer px-6 py-5 rounded-t-2xl overflow-hidden">
                    <div className="absolute inset-0 animate-shimmer-light"></div>
                    <div className="relative flex items-center gap-3">
                      <span className="material-symbols-outlined text-white/90 text-xl">auto_awesome</span>
                      <p className="text-white font-extrabold text-base">
                        {t('hero.searchInstruction')}
                      </p>
                    </div>
                  </div>

                  {/* Search */}
                  <div className="p-5 relative overflow-visible">
                    {!selectedBusiness ? (
                      <div ref={heroSearchRef} className="relative z-40">
                        <div className="relative">
                          <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xl">search</span>
                          <input
                            type="text"
                            autoFocus
                            value={heroSearchQuery}
                            onChange={(e) => { const v = e.target.value; setHeroSearchQuery(v); setShowHeroResults(true); if (v.trim().length > 0) setIsHeroSearching(true); }}
                            onFocus={() => setShowHeroResults(true)}
                            placeholder={i18n.language.startsWith('es') ? 'Nombre de tu negocio (ej. La Pizzería de Juan)...' : "Business name (e.g., Joe's Pizza, Rivera Cuts, AAA Plumbing)..."}
                            className="w-full pl-12 pr-10 py-4 rounded-xl border-2 border-emerald-300 dark:border-emerald-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all animate-input-glow"
                          />
                          {isHeroSearching && (
                            <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                              <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          )}
                        </div>
                        {showHeroResults && heroSearchResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl max-h-56 overflow-y-auto">
                            {heroSearchResults.map((place) => (
                              <button
                                key={place.placeId}
                                onClick={() => handleSelectBusiness(place)}
                                className="w-full px-4 py-3 flex items-start gap-3 hover:bg-emerald-50 dark:hover:bg-slate-700 text-left border-b border-slate-100 dark:border-slate-700 last:border-b-0 transition-colors"
                              >
                                <span className="material-symbols-outlined text-emerald-500 text-lg mt-0.5">location_on</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{place.name}</p>
                                  <p className="text-xs text-slate-500 truncate">{place.address}</p>
                                  {place.rating && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <span className="material-symbols-outlined text-yellow-500 text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{place.rating.toFixed(1)}</span>
                                      {place.totalRatings && <span className="text-xs text-slate-400">({place.totalRatings})</span>}
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Business found — Analytical Social Kit view */
                      <div className="space-y-4">
                        {/* Analytical header — business name + rating dashboard */}
                        <div className="bg-slate-900 dark:bg-slate-800 rounded-2xl p-4 relative overflow-hidden">
                          {/* subtle grid pattern */}
                          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #10b981 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                          <div className="relative z-10">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold mb-0.5">
                                  {i18n.language.startsWith('es') ? 'Negocio detectado' : 'Business detected'}
                                </p>
                                <p className="text-lg font-extrabold text-white leading-tight truncate">{selectedBusiness.name}</p>
                              </div>
                              <button
                                onClick={() => { setSelectedBusiness(null); setScanState('idle'); }}
                                aria-label="Clear selected business"
                                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors shrink-0 mt-0.5"
                              >
                                <span className="material-symbols-outlined text-base text-slate-400" aria-hidden="true">close</span>
                              </button>
                            </div>
                            {selectedBusiness.rating ? (
                              <div className="flex items-center gap-4">
                                {/* Big rating number */}
                                <div className="flex items-end gap-1.5">
                                  <span className="text-4xl font-black text-white leading-none">{selectedBusiness.rating.toFixed(1)}</span>
                                  <span className="text-slate-400 text-xs mb-1">/ 5.0</span>
                                </div>
                                {/* Stars + count */}
                                <div className="flex flex-col gap-1">
                                  <div className="flex gap-0.5">
                                    {[1,2,3,4,5].map(i => (
                                      <svg key={i} className={`w-4 h-4 ${i <= Math.round(selectedBusiness.rating!) ? 'text-yellow-400' : 'text-slate-600'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                      </svg>
                                    ))}
                                  </div>
                                  <span className="text-slate-400 text-[10px]">
                                    {selectedBusiness.totalRatings?.toLocaleString() ?? '—'} {i18n.language.startsWith('es') ? 'reseñas' : 'reviews'}
                                  </span>
                                </div>
                                {/* Content potential badge */}
                                <div className="ml-auto bg-emerald-500/20 border border-emerald-500/30 rounded-lg px-2 py-1 text-center shrink-0">
                                  <p className="text-lg font-black text-emerald-400 leading-none">{fakeReviewCount}</p>
                                  <p className="text-[9px] text-emerald-300 leading-none mt-0.5">{i18n.language.startsWith('es') ? 'posts listos' : 'posts ready'}</p>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400">{i18n.language.startsWith('es') ? 'Negocio encontrado' : 'Business found'}</p>
                            )}
                          </div>
                        </div>

                        {/* Preview of what will be generated */}
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { icon: 'image', label: i18n.language.startsWith('es') ? 'Post IG' : 'IG Post', color: 'from-purple-600 via-pink-500 to-orange-400', aspect: '1:1' },
                            { icon: 'smartphone', label: i18n.language.startsWith('es') ? 'Story IG' : 'IG Story', color: 'from-purple-600 via-pink-500 to-orange-400', aspect: '9:16' },
                            { icon: 'facebook', label: i18n.language.startsWith('es') ? 'Facebook' : 'Facebook', color: 'from-blue-600 to-blue-700', aspect: '16:9' },
                          ].map((card, i) => (
                            <div key={i} className={`relative rounded-xl overflow-hidden bg-gradient-to-br ${card.color} p-2.5 flex flex-col items-center justify-center gap-1 aspect-square`}>
                              <div className="absolute inset-0 bg-black/20"></div>
                              <span className="material-symbols-outlined text-white text-xl relative z-10">{card.icon}</span>
                              <p className="text-white text-[9px] font-bold text-center relative z-10 leading-tight">{card.label}</p>
                              <p className="text-white/60 text-[7px] font-medium relative z-10">{card.aspect}</p>
                              <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/30 flex items-center justify-center">
                                <span className="material-symbols-outlined text-white/80 text-[10px]">lock</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Generate CTA */}
                        <button
                          onClick={handleGenerate}
                          className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white font-extrabold rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/30 hover:-translate-y-0.5 flex items-center justify-center gap-2 text-base"
                        >
                          <span className="material-symbols-outlined text-lg">auto_awesome</span>
                          {t('hero.generateButton')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile-only CTA buttons — shown below the search card on mobile/tablet */}
              <div className="lg:hidden flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
                <button onClick={handleStartTrial} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-extrabold text-lg shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all hover:-translate-y-0.5 ring-2 ring-emerald-400/30 animate-pulse-subtle inline-flex items-center gap-2">
                  {t('nav.startTrial')}
                  <span className="material-symbols-outlined">arrow_forward</span>
                </button>
                <button onClick={() => scrollToSection('video-demo')} className="group glass-panel px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 inline-flex items-center gap-2">
                  <span className="material-symbols-outlined">play_circle</span>
                  {t('hero.watchDemo')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Bar */}
        <div className="relative z-10 pb-4 px-4 sm:px-6 lg:px-8">
          <p className="text-center text-base sm:text-lg font-semibold text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {t('hero.trustBar')}
          </p>
        </div>

        {/* Video Demo Section */}
        <section id="video-demo" className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mb-4">
                {t('video.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">{t('video.titleHighlight')}</span>
              </h2>
            </div>

            {/* Desktop Video - Horizontal 16:9 */}
            <div className="hidden md:block glass-panel rounded-[2rem] p-4 shadow-2xl overflow-hidden">
              <LiteYouTube
                videoId={i18n.language.startsWith('es') ? '0g4vn-0thMk' : 'CAvhrrhE5YI'}
                title="Visto Demo Video - See how to boost your Google reviews"
                aspect="landscape"
              />
            </div>

            {/* Mobile Video - Vertical 9:16 (YouTube Shorts) */}
            <div className="md:hidden glass-panel rounded-[2rem] p-4 shadow-2xl overflow-hidden max-w-sm mx-auto">
              <LiteYouTube
                videoId={i18n.language.startsWith('es') ? 'Wgre0kX98Gg' : 'HKf0HPDghhU'}
                title="Visto Demo Video - See how to boost your Google reviews"
                aspect="portrait"
              />
            </div>
          </div>
        </section>

        {/* ── Social Cards Showcase ── */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full glass-pill">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 tracking-widest uppercase">Live Examples</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white mb-4">
                Real posts. Real businesses. <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">Zero effort.</span>
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                Every social card below was created automatically from a real Google review — no designer needed.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 reveal">
              {/* Card 1 — Restaurant */}
              <div className="rounded-[1.75rem] overflow-hidden shadow-2xl shadow-orange-500/20 group transition-all duration-500 hover:-translate-y-2">
                {/* IG header */}
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-900">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 p-0.5">
                    <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center text-white font-black text-[9px]">LT</div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-xs font-bold leading-none">@latrattoria.nyc</p>
                    <p className="text-slate-400 text-[10px] mt-0.5">New York, NY</p>
                  </div>
                  <span className="text-[10px] font-bold text-blue-400">Follow</span>
                </div>
                {/* Post image area — pizza bg + orange wash */}
                <div className="aspect-square flex flex-col items-center justify-center p-6 relative overflow-hidden bg-amber-50">
                  {/* Giant pizza background */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden="true">
                    <span style={{ fontSize: '200px', lineHeight: 1, opacity: 0.55, transform: 'rotate(-12deg)' }}>🍕</span>
                  </div>
                  {/* Orange color wash at 50% opacity */}
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.5) 0%, rgba(239,68,68,0.5) 50%, rgba(236,72,153,0.5) 100%)' }}></div>
                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="flex gap-1 mb-3">
                      {[0,1,2,3,4].map(i => (
                        <svg key={i} className="w-5 h-5 text-yellow-300 animate-star-shine" style={{ animationDelay: `${i * 0.15}s` }} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      ))}
                    </div>
                    <p className="text-white text-center font-bold text-sm leading-snug mb-2 max-w-[200px] drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
                      "Best authentic Italian I've had outside of Rome. Absolutely incredible."
                    </p>
                    <p className="text-white/90 text-xs mb-3 drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]">— James R.</p>
                    <div className="flex items-center gap-1.5 bg-white/25 backdrop-blur-sm border border-white/30 rounded-full px-3 py-1">
                      <span className="text-[10px] text-white font-bold">4.9</span>
                      <svg className="w-3 h-3 text-red-300" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                      <span className="text-[10px] text-white font-bold">Google Maps</span>
                    </div>
                  </div>
                </div>
                {/* IG footer */}
                <div className="flex items-center gap-4 px-4 py-3 bg-slate-900">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>
                  <span className="text-white/60 text-xs">2.4K</span>
                  <svg className="w-5 h-5 text-white ml-1" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"/></svg>
                  <span className="text-white/60 text-xs">89</span>
                  <div className="ml-auto w-6 h-6 rounded bg-orange-500/30 flex items-center justify-center">
                    <span className="text-orange-300 text-[10px] font-bold">V</span>
                  </div>
                </div>
              </div>

              {/* Card 2 — Service Business */}
              <div className="rounded-[1.75rem] overflow-hidden shadow-2xl shadow-violet-500/20 group transition-all duration-500 hover:-translate-y-2">
                {/* IG header */}
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-950">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-700 p-0.5">
                    <div className="w-full h-full rounded-full bg-slate-950 flex items-center justify-center text-white font-black text-[9px]">CP</div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-xs font-bold leading-none">@cleanpro.services</p>
                    <p className="text-slate-400 text-[10px] mt-0.5">Miami, FL</p>
                  </div>
                  <span className="text-[10px] font-bold text-blue-400">Follow</span>
                </div>
                <div className="aspect-square flex flex-col items-center justify-center p-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 50%, #6d28d9 100%)' }}>
                  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_70%_80%,rgba(167,139,250,0.4)_0%,transparent_60%)]"></div>
                  <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3 border border-white/20">
                    <span className="text-2xl">🔧</span>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {[0,1,2,3,4].map(i => (
                      <svg key={i} className="w-5 h-5 text-yellow-300 animate-star-shine" style={{ animationDelay: `${i * 0.15 + 0.5}s` }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-white text-center font-bold text-sm leading-snug mb-3 max-w-[200px]">
                    "Fixed our AC in under 20 minutes. Arrived on time, professional, and very affordable."
                  </p>
                  <p className="text-white/70 text-xs mb-3">— Sandra K.</p>
                  <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1">
                    <span className="text-[10px] text-white/80 font-semibold">4.8</span>
                    <svg className="w-3 h-3 text-red-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                    <span className="text-[10px] text-white/80 font-semibold">Google Maps</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 px-4 py-3 bg-slate-950">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>
                  <span className="text-white/60 text-xs">1.1K</span>
                  <svg className="w-5 h-5 text-white ml-1" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"/></svg>
                  <span className="text-white/60 text-xs">43</span>
                  <div className="ml-auto w-6 h-6 rounded bg-violet-500/30 flex items-center justify-center">
                    <span className="text-violet-300 text-[10px] font-bold">V</span>
                  </div>
                </div>
              </div>

              {/* Card 3 — E-commerce / Generic */}
              <div className="rounded-[1.75rem] overflow-hidden shadow-2xl shadow-emerald-500/20 group transition-all duration-500 hover:-translate-y-2">
                {/* IG header */}
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-900">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 p-0.5">
                    <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center text-white font-black text-[9px]">GS</div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-xs font-bold leading-none">@glowstudio.official</p>
                    <p className="text-slate-400 text-[10px] mt-0.5">Los Angeles, CA</p>
                  </div>
                  <span className="text-[10px] font-bold text-blue-400">Follow</span>
                </div>
                {/* Post image area — botanical bg + green wash */}
                <div className="aspect-square flex flex-col items-center justify-center p-6 relative overflow-hidden bg-emerald-50">
                  {/* Giant leaf/spa background */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden="true">
                    <span style={{ fontSize: '200px', lineHeight: 1, opacity: 0.55, transform: 'rotate(15deg)' }}>🌿</span>
                  </div>
                  {/* Green color wash at 50% opacity */}
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(5,150,105,0.5) 0%, rgba(20,184,166,0.5) 50%, rgba(6,182,212,0.5) 100%)' }}></div>
                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="flex gap-1 mb-3">
                      {[0,1,2,3,4].map(i => (
                        <svg key={i} className="w-5 h-5 text-yellow-300 animate-star-shine" style={{ animationDelay: `${i * 0.15 + 1}s` }} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      ))}
                    </div>
                    <p className="text-white text-center font-bold text-sm leading-snug mb-2 max-w-[200px] drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
                      "Life-changing treatment. My skin has never looked better — will be back every month!"
                    </p>
                    <p className="text-white/90 text-xs mb-3 drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]">— Lisa M.</p>
                    <div className="flex items-center gap-1.5 bg-white/25 backdrop-blur-sm border border-white/30 rounded-full px-3 py-1">
                      <span className="text-[10px] text-white font-bold">5.0</span>
                      <svg className="w-3 h-3 text-red-300" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                      <span className="text-[10px] text-white font-bold">Google Maps</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 px-4 py-3 bg-slate-900">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>
                  <span className="text-white/60 text-xs">3.8K</span>
                  <svg className="w-5 h-5 text-white ml-1" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"/></svg>
                  <span className="text-white/60 text-xs">127</span>
                  <div className="ml-auto w-6 h-6 rounded bg-emerald-500/30 flex items-center justify-center">
                    <span className="text-emerald-300 text-[10px] font-bold">V</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-10 reveal">
              <button onClick={handleStartTrial} className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white hover:bg-slate-700 dark:hover:bg-slate-100 text-white dark:text-slate-900 font-extrabold px-8 py-4 rounded-full transition-all shadow-xl hover:-translate-y-0.5">
                <span className="material-symbols-outlined text-lg">auto_awesome</span>
                Create my social cards — free
              </button>
              <p className="text-slate-400 text-sm mt-3">No credit card · Takes 30 seconds</p>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section id="comparison" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-100/50 dark:bg-slate-900/50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 reveal">
              <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 rounded-full glass-pill">
                <span className="text-xs font-bold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">{t('comparison.badge')}</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white mb-6 tracking-tight">
                {t('comparison.title')}
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                {t('comparison.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
              {/* Naked QR Code */}
              <div className="glass-panel rounded-[2.5rem] p-10 hover:shadow-lg transition-all duration-300 flex flex-col">
                <div className="text-center mb-10">
                  <div className="w-20 h-20 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6 text-slate-400">
                    <span className="material-symbols-outlined text-5xl">qr_code_2</span>
                  </div>
                  <h3 className="text-2xl font-extrabold text-slate-700 dark:text-slate-200 mb-2">{t('comparison.nakedQr.title')}</h3>
                  <p className="text-slate-500 text-sm font-bold uppercase tracking-wide">{t('comparison.nakedQr.subtitle')}</p>
                </div>

                <ul className="space-y-6 flex-1">
                  <li className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                    <span className="material-symbols-outlined text-rose-500 text-2xl mt-0.5">cancel</span>
                    <div>
                      <h4 className="font-extrabold text-slate-800 dark:text-slate-200 mb-1">{t('comparison.nakedQr.noFilter')}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">{t('comparison.nakedQr.noFilterDesc')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                    <span className="material-symbols-outlined text-rose-500 text-2xl mt-0.5">cancel</span>
                    <div>
                      <h4 className="font-extrabold text-slate-800 dark:text-slate-200 mb-1">{t('comparison.nakedQr.unmanaged')}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">{t('comparison.nakedQr.unmanagedDesc')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                    <span className="material-symbols-outlined text-rose-500 text-2xl mt-0.5">cancel</span>
                    <div>
                      <h4 className="font-extrabold text-slate-800 dark:text-slate-200 mb-1">{t('comparison.nakedQr.zeroData')}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">{t('comparison.nakedQr.zeroDataDesc')}</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Visto Filter */}
              <div className="glass-panel rounded-[2.5rem] p-12 lg:scale-105 shadow-[0_0_50px_rgba(16,185,129,0.15)] hover:shadow-[0_0_70px_rgba(16,185,129,0.25)] transition-all duration-300 relative overflow-hidden flex flex-col">
                <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/50 to-transparent dark:from-emerald-900/10 rounded-[2.5rem] pointer-events-none"></div>

                <div className="relative text-center mb-10">
                  <div className="w-24 h-24 mx-auto mb-6">
                    <img
                      src={logoImage}
                      alt="Visto"
                      width="96"
                      height="96"
                      className="w-full h-full object-contain drop-shadow-xl"
                    />
                  </div>
                  <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">{t('comparison.visto.title')}</h3>
                  <div className="inline-block bg-emerald-100 dark:bg-emerald-900/50 px-4 py-1.5 rounded-full">
                    <p className="text-emerald-600 dark:text-emerald-300 text-sm font-extrabold uppercase tracking-wide">
                      {t('comparison.visto.subtitle')}
                    </p>
                  </div>
                </div>

                <ul className="space-y-6 mb-10 flex-1">
                  <li className="flex items-start gap-4 p-4 rounded-xl bg-white/40 dark:bg-slate-800/40 border border-white/50 dark:border-slate-700/50">
                    <div className="p-1 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex-shrink-0">
                      <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-lg font-bold">check</span>
                    </div>
                    <div>
                      <h4 className="font-extrabold text-slate-900 dark:text-white mb-1">{t('comparison.visto.smartInterception')}</h4>
                      <p className="text-slate-600 dark:text-slate-300 text-sm">{t('comparison.visto.smartInterceptionDesc')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 p-4 rounded-xl bg-white/40 dark:bg-slate-800/40 border border-white/50 dark:border-slate-700/50">
                    <div className="p-1 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex-shrink-0">
                      <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-lg font-bold">check</span>
                    </div>
                    <div>
                      <h4 className="font-extrabold text-slate-900 dark:text-white mb-1">{t('comparison.visto.realTimeAlerts')}</h4>
                      <p className="text-slate-600 dark:text-slate-300 text-sm">{t('comparison.visto.realTimeAlertsDesc')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 p-4 rounded-xl bg-white/40 dark:bg-slate-800/40 border border-white/50 dark:border-slate-700/50">
                    <div className="p-1 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex-shrink-0">
                      <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-lg font-bold">check</span>
                    </div>
                    <div>
                      <h4 className="font-extrabold text-slate-900 dark:text-white mb-1">{t('comparison.visto.dataEnrichment')}</h4>
                      <p className="text-slate-600 dark:text-slate-300 text-sm">{t('comparison.visto.dataEnrichmentDesc')}</p>
                    </div>
                  </li>
                </ul>

                <button onClick={handleStartTrial} className="w-full group relative overflow-hidden rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-lg py-5 px-8 transition-all duration-300 shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/50 transform hover:-translate-y-0.5">
                  <span className="relative flex items-center justify-center gap-2">
                    {t('comparison.visto.cta')}
                    <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Who Is It For Section */}
        <section id="verticals" className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full glass-pill">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 tracking-widest uppercase">{t('verticals.badge')}</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white mb-6">
                {t('verticals.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">{t('verticals.titleHighlight')}</span>
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                {t('verticals.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Hospitality */}
              <div className="glass-panel rounded-[2.5rem] p-8 flex flex-col relative overflow-hidden group transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                <div className="relative z-10 flex justify-between items-start mb-8">
                  <div className="w-14 h-14 glass-panel rounded-2xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-3xl text-emerald-600">restaurant</span>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    {t('verticals.hospitality.badge')}
                  </span>
                </div>
                <div className="relative z-10 flex-grow">
                  <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-3 group-hover:text-emerald-500 transition-colors">{t('verticals.hospitality.title')}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('verticals.hospitality.subtitle')}</p>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium mb-6">
                    {t('verticals.hospitality.description')}
                  </p>
                  {/* Platform logos */}
                  <div className="flex items-center gap-3 mb-6">
                    <SiGooglemaps className="text-2xl text-red-500" title="Google Maps" />
                    <FaFacebook className="text-2xl text-[#1877F2]" title="Facebook" />
                    <SiTripadvisor className="text-2xl text-[#00AF87]" title="TripAdvisor" />
                    <FaYelp className="text-2xl text-[#FF1A1A]" title="Yelp" />
                  </div>
                </div>
                <button onClick={handleStartTrial} className="relative z-10 w-full py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold transition-all text-sm shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5">
                  {t('nav.startTrial')}
                </button>
              </div>

              {/* Service Businesses */}
              <div className="glass-panel rounded-[2.5rem] p-8 flex flex-col relative overflow-hidden group transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                <div className="relative z-10 flex justify-between items-start mb-8">
                  <div className="w-14 h-14 glass-panel rounded-2xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-3xl text-emerald-600">handshake</span>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    {t('verticals.services.badge')}
                  </span>
                </div>
                <div className="relative z-10 flex-grow">
                  <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-3 group-hover:text-emerald-500 transition-colors">{t('verticals.services.title')}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('verticals.services.subtitle')}</p>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium mb-6">
                    {t('verticals.services.description')}
                  </p>
                  {/* Platform logos */}
                  <div className="flex items-center gap-3 mb-6">
                    <SiGooglemaps className="text-2xl text-red-500" title="Google Maps" />
                    <FaFacebook className="text-2xl text-[#1877F2]" title="Facebook" />
                    <FaYelp className="text-2xl text-[#FF1A1A]" title="Yelp" />
                    <SiG2 className="text-2xl text-[#FF492C]" title="G2" />
                  </div>
                </div>
                <button onClick={handleStartTrial} className="relative z-10 w-full py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold transition-all text-sm shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5">
                  {t('nav.startTrial')}
                </button>
              </div>

              {/* SaaS & Startups */}
              <div className="glass-panel rounded-[2.5rem] p-8 flex flex-col relative overflow-hidden group transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                <div className="relative z-10 flex justify-between items-start mb-8">
                  <div className="w-14 h-14 glass-panel rounded-2xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-3xl text-emerald-600">cloud_done</span>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    {t('verticals.saas.badge')}
                  </span>
                </div>
                <div className="relative z-10 flex-grow">
                  <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-3 group-hover:text-emerald-500 transition-colors">{t('verticals.saas.title')}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('verticals.saas.subtitle')}</p>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium mb-6">
                    {t('verticals.saas.description')}
                  </p>
                  {/* Platform logos */}
                  <div className="flex items-center gap-3 mb-6">
                    <SiG2 className="text-2xl text-[#FF492C]" title="G2" />
                    <SiTripadvisor className="text-2xl text-[#00AF87]" title="Trustpilot / Review sites" />
                    <FcGoogle className="text-2xl" title="Google Reviews" />
                    <FaFacebook className="text-2xl text-[#1877F2]" title="Facebook" />
                  </div>
                </div>
                <button onClick={handleStartTrial} className="relative z-10 w-full py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold transition-all text-sm shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5">
                  {t('nav.startTrial')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-100/50 dark:bg-slate-900/50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20 reveal">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white mb-6">
                {t('features.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">{t('features.titleHighlight')}</span>
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                {t('features.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Auto-Poster Feature */}
              <div className="lg:col-span-2 glass-panel rounded-[2.5rem] p-10 md:p-14 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 relative overflow-hidden group min-h-[500px] flex flex-col justify-between reveal">
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-600 dark:text-emerald-300 text-xs font-extrabold mb-8 backdrop-blur-md shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                    <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                    {t('features.autoPoster.badge')}
                  </div>
                  <h3 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6 leading-tight">
                    {t('features.autoPoster.title')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-200 mb-10 leading-loose font-medium text-lg max-w-md">
                    {t('features.autoPoster.description')}
                  </p>
                  <button className="group/btn bg-white/5 hover:bg-emerald-500/20 border border-white/20 hover:border-emerald-400/50 text-slate-900 dark:text-white hover:text-emerald-500 dark:hover:text-emerald-400 px-8 py-3 rounded-2xl font-extrabold transition-all hover:scale-105 flex items-center gap-3">
                    {t('features.autoPoster.cta')}
                    <span className="material-symbols-outlined transition-transform group-hover/btn:translate-x-1">arrow_forward</span>
                  </button>
                </div>

                <div className="absolute right-0 bottom-0 top-0 w-1/2 flex items-center justify-center pointer-events-none overflow-hidden">
                  <div className="relative transform translate-x-8 scale-90 group-hover:scale-100 transition-transform duration-700">
                    <div className="w-52 h-52 glass-emerald rounded-[2rem] flex flex-col items-center justify-center p-6 shadow-[0_20px_60px_-10px_rgba(16,185,129,0.4)] transform rotate-3 transition-transform group-hover:rotate-6 duration-700">
                      <span className="material-symbols-outlined text-white text-[3.5rem] mb-3">format_quote</span>
                      <div className="flex gap-1 mb-5 text-emerald-100">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                        ))}
                      </div>
                      <div className="w-16 h-1.5 bg-white/40 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sentiment Tracking */}
              <div className="glass-panel rounded-[2.5rem] p-10 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 flex flex-col justify-between group min-h-[420px] reveal reveal-delay-1">
                <div>
                  <div className="w-14 h-14 bg-emerald-50 dark:bg-slate-700/60 rounded-2xl flex items-center justify-center mb-8 text-emerald-600 dark:text-emerald-400 shadow-sm dark:shadow-xl border border-emerald-200 dark:border-emerald-500/20">
                    <span className="material-symbols-outlined text-3xl">analytics</span>
                  </div>
                  <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-3">{t('features.analytics.title')}</h3>
                  <p className="text-slate-600 dark:text-slate-200 font-medium leading-loose mb-8">
                    {t('features.analytics.description')}
                  </p>
                </div>

                <div>
                  <div className="flex items-baseline gap-4 mb-5">
                    <span className="text-6xl font-extrabold text-slate-900 dark:text-white tracking-tighter">4.9</span>
                    <div className="flex items-center text-emerald-400 text-sm font-extrabold bg-emerald-500/10 px-3 py-1 rounded-lg border border-emerald-400/30">
                      <span className="material-symbols-outlined text-sm mr-1">trending_up</span>
                      +12%
                    </div>
                  </div>
                  <div className="w-full h-3 bg-slate-200 dark:bg-slate-800/50 rounded-full overflow-hidden border border-slate-300 dark:border-slate-700/30">
                    <div className="h-full bg-emerald-400 rounded-full w-3/4 shadow-[0_0_20px_rgba(52,211,153,0.6)] relative group-hover:w-4/5 transition-all duration-700">
                      <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feedback Intercept */}
              <div className="glass-panel rounded-[2.5rem] p-10 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 flex flex-col group min-h-[400px] reveal reveal-delay-2">
                <div className="w-14 h-14 bg-rose-50 dark:bg-slate-700/60 rounded-2xl flex items-center justify-center mb-8 text-rose-600 dark:text-rose-400 shadow-sm dark:shadow-xl border border-rose-200 dark:border-rose-500/20">
                  <span className="material-symbols-outlined text-3xl">shield</span>
                </div>
                <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-3">{t('features.intercept.title')}</h3>
                <p className="text-slate-600 dark:text-slate-200 font-medium leading-loose mb-auto">
                  {t('features.intercept.description')}
                </p>

                <div className="mt-10 relative h-28">
                  <div className="absolute bottom-2 left-0 w-full bg-slate-900/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-5 shadow-2xl border border-white/10 flex items-center gap-5 transform transition-all duration-500 group-hover:-translate-y-2">
                    <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400 shrink-0 animate-pulse border border-rose-500/30">
                      <span className="material-symbols-outlined text-xl">priority_high</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-extrabold text-white">{t('features.intercept.alertTitle')}</p>
                      <p className="text-xs font-medium text-slate-400 truncate mt-1">{t('features.intercept.alertDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Integration Network */}
              <div className="lg:col-span-2 glass-panel rounded-[2.5rem] p-10 md:p-14 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 flex flex-col md:flex-row items-center md:justify-between min-h-[420px]">
                <div className="md:w-5/12 text-center md:text-left mb-10 md:mb-0">
                  <h3 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-6">{t('features.integrations.title')}</h3>
                  <p className="text-slate-600 dark:text-slate-200 leading-loose font-medium mb-10 text-lg">
                    {t('features.integrations.description')}
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-4 flex-wrap">
                    <div className="w-12 h-12 rounded-full glass-panel flex items-center justify-center p-2 hover:scale-110 transition-transform cursor-pointer">
                      <span className="material-symbols-outlined text-blue-500">cloud</span>
                    </div>
                    <div className="w-12 h-12 rounded-full glass-panel flex items-center justify-center p-2 hover:scale-110 transition-transform cursor-pointer">
                      <span className="material-symbols-outlined text-rose-500">mail</span>
                    </div>
                    <div className="w-12 h-12 rounded-full glass-panel flex items-center justify-center p-2 hover:scale-110 transition-transform cursor-pointer">
                      <span className="material-symbols-outlined text-emerald-500">chat</span>
                    </div>
                    <div className="px-5 h-12 rounded-full glass-panel flex items-center justify-center text-sm font-extrabold text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors cursor-pointer">
                      {t('features.integrations.more')}
                    </div>
                  </div>
                </div>

                <div className="md:w-7/12 relative h-[340px] flex items-center justify-center">
                  <div className="relative w-80 h-80 animate-float-slow">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-white rounded-full flex items-center justify-center z-20 shadow-xl border border-slate-200 p-4">
                      <img
                        src={logoImage}
                        alt="Visto"
                        width="128"
                        height="128"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-16 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg border border-slate-200">
                      <FcGoogle className="text-3xl" />
                    </div>
                    <div className="absolute top-1/2 right-0 translate-x-1/4 -translate-y-1/2 w-14 h-14 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg border border-slate-200">
                      <FaFacebook className="text-3xl text-[#1877F2]" />
                    </div>
                    <div className="absolute bottom-6 left-8 w-16 h-16 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg border border-slate-200 text-[#FF1A1A]">
                      <FaYelp className="text-3xl" />
                    </div>
                    <div className="absolute bottom-6 right-8 w-14 h-14 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg border border-slate-200">
                      <SiGooglemaps className="text-2xl text-[#4285F4]" />
                    </div>
                    <div className="absolute top-1/2 left-0 -translate-x-1/4 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg border border-slate-200">
                      <SiTripadvisor className="text-2xl text-[#00AF87]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Safety Net Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 dark:bg-slate-950 relative overflow-hidden">
          {/* Subtle grid texture */}
          <div className="absolute inset-0 bg-grid opacity-10"></div>
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-emerald-600/10 rounded-full blur-[100px] pointer-events-none"></div>
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-16 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-white/10 border border-white/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-xs font-extrabold text-emerald-400 tracking-widest uppercase">{t('safetyNet.badge')}</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
                {t('safetyNet.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">{t('safetyNet.titleHighlight')}</span>
              </h2>
              <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
                {t('safetyNet.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'qr_code_2',
                  color: 'from-violet-500/20 to-violet-600/10 border-violet-500/30',
                  iconColor: 'text-violet-400',
                  title: t('safetyNet.feature1Title'),
                  desc: t('safetyNet.feature1Desc'),
                },
                {
                  icon: 'bolt',
                  color: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
                  iconColor: 'text-amber-400',
                  title: t('safetyNet.feature2Title'),
                  desc: t('safetyNet.feature2Desc'),
                },
                {
                  icon: 'star',
                  color: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
                  iconColor: 'text-emerald-400',
                  title: t('safetyNet.feature3Title'),
                  desc: t('safetyNet.feature3Desc'),
                },
              ].map((item, i) => (
                <div key={i} className={`bg-gradient-to-br ${item.color} border rounded-2xl p-8 reveal`}
                  style={{ transitionDelay: `${i * 100}ms` }}>
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                    <span className={`material-symbols-outlined text-2xl ${item.iconColor}`} style={{ fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
                  </div>
                  <h3 className="text-lg font-extrabold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* QR flow mini-diagram */}
            <div className="mt-12 flex items-center justify-center gap-3 flex-wrap">
              {[
                { icon: 'qr_code_2', label: i18n.language.startsWith('es') ? 'Cliente escanea QR' : 'Customer scans QR', color: 'bg-white/10 border-white/20' },
                null,
                { icon: 'thumbs_up_down', label: i18n.language.startsWith('es') ? 'Visto filtra' : 'Visto filters', color: 'bg-emerald-500/20 border-emerald-500/40' },
                null,
                { icon: 'star', label: i18n.language.startsWith('es') ? '5⭐ → Google' : '5★ → Google', color: 'bg-emerald-500/20 border-emerald-500/40' },
              ].map((item, i) =>
                item === null ? (
                  <svg key={i} className="w-6 h-4 text-slate-500 hidden sm:block" fill="none" viewBox="0 0 24 16">
                    <path d="M0 8h18M14 4l6 4-6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <div key={i} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border ${item.color}`}>
                    <span className="material-symbols-outlined text-white text-base">{item.icon}</span>
                    <span className="text-white text-xs font-bold whitespace-nowrap">{item.label}</span>
                  </div>
                )
              )}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white mb-6">
                {t('pricing.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">{t('pricing.titleHighlight')}</span> {t('pricing.titleEnd')}
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                {t('pricing.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* The Shield */}
              <div className="glass-panel rounded-3xl p-8 flex flex-col h-full transition-all duration-500 hover:-translate-y-1 hover:shadow-xl">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <span className="material-symbols-outlined text-slate-600 dark:text-slate-300 text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>shield</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-2">{t('pricing.shield.title')}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 font-medium">{t('pricing.shield.subtitle')}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">$19</span>
                    <span className="text-slate-500 dark:text-slate-400 font-medium">{t('pricing.shield.period')}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  <p className="text-xs font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4">{t('pricing.whatsIncluded')}</p>
                  {[t('pricing.shield.feature1'), t('pricing.shield.feature2'), t('pricing.shield.feature3'), t('pricing.shield.feature4'), t('pricing.shield.feature5')].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                      <span className="material-symbols-outlined text-emerald-600 text-sm mt-0.5">check_circle</span>
                      <span className="text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <button onClick={handleStartTrial} className="w-full glass-panel rounded-xl py-3.5 px-4 font-extrabold text-slate-700 dark:text-slate-300 transition-all hover:shadow-lg">
                  {t('pricing.shield.cta')}
                </button>
              </div>

              {/* The Influencer (Recommended) */}
              <div className="glass-panel rounded-3xl p-0 flex flex-col h-full transition-all duration-500 lg:scale-105 z-20 hover:-translate-y-1 hover:shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-t-3xl"></div>

                <div className="p-8 pb-0 relative">
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 rounded-full text-[11px] font-extrabold bg-emerald-500 text-white uppercase tracking-wider shadow-sm">
                      {i18n.language.startsWith('es') ? 'Recomendado' : 'Recommended'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-3 mt-2">
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/50 flex items-center justify-center">
                      <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-2">{t('pricing.influencer.title')}</h3>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-6 font-extrabold">{t('pricing.influencer.subtitle')}</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight">$49</span>
                      <span className="text-slate-500 dark:text-slate-400 font-medium">{t('pricing.influencer.period')}</span>
                    </div>
                  </div>

                  <button onClick={handleStartTrial} className="w-full bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white rounded-xl py-3.5 px-4 font-extrabold transition-all hover:shadow-xl flex items-center justify-center gap-2 mb-8 group">
                    {t('pricing.influencer.cta')}
                    <span className="material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </button>
                </div>

                <div className="p-8 pt-0 space-y-4 flex-1">
                  <p className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-4">{t('pricing.whatsIncluded')}</p>
                  {[t('pricing.influencer.feature1'), t('pricing.influencer.feature2'), t('pricing.influencer.feature3'), t('pricing.influencer.feature4'), t('pricing.influencer.feature5'), t('pricing.influencer.feature6')].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-200">
                      <span className="material-symbols-outlined text-emerald-600 text-sm mt-0.5" style={i === 0 ? {} : {}}>check_circle</span>
                      <span className={`text-sm font-medium ${i === 0 ? 'font-bold text-emerald-700 dark:text-emerald-300' : ''}`}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Enterprise Plan */}
              <div className="glass-panel rounded-3xl p-8 flex flex-col h-full transition-all duration-500 hover:-translate-y-1 hover:shadow-xl">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <span className="material-symbols-outlined text-slate-600 dark:text-slate-300 text-xl">corporate_fare</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-2">{t('pricing.enterprise.title')}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 font-medium">{t('pricing.enterprise.subtitle')}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">$99</span>
                    <span className="text-slate-500 dark:text-slate-400 font-medium">{t('pricing.enterprise.period')}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  <p className="text-xs font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4">{t('pricing.whatsIncluded')}</p>
                  {[t('pricing.enterprise.feature1'), t('pricing.enterprise.feature2'), t('pricing.enterprise.feature3'), t('pricing.enterprise.feature4'), t('pricing.enterprise.feature5')].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                      <span className="material-symbols-outlined text-emerald-600 text-sm mt-0.5">check_circle</span>
                      <span className="text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <a href="https://calendar.app.google/vvEQCYQAiJemGrgr8" target="_blank" rel="noopener noreferrer" className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl py-3.5 px-4 font-extrabold transition-all hover:shadow-xl flex items-center justify-center gap-2 group">
                  {t('pricing.enterprise.cta')}
                  <span className="material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-500 to-teal-600 dark:from-emerald-600 dark:to-teal-700 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-20"></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-emerald-50 mb-10 max-w-2xl mx-auto">
              {t('cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={handleStartTrial} className="bg-white text-emerald-600 px-10 py-4 rounded-full font-extrabold text-lg shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all">
                {t('cta.primaryButton')}
              </button>
              <a href="https://calendar.app.google/vvEQCYQAiJemGrgr8" target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-md border-2 border-white/30 text-white px-10 py-4 rounded-full font-extrabold text-lg hover:bg-white/20 transition-all">
                {t('cta.secondaryButton')}
              </a>
            </div>
            <p className="mt-6 text-emerald-100 text-sm font-medium">
              {t('cta.note')}
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 dark:text-white mb-6">
                {t('faq.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">{t('faq.titleHighlight')}</span>
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                {t('faq.subtitle')}
              </p>
            </div>

            <div className="space-y-4">
              {/* FAQ Item 1 */}
              <details className="group glass-panel rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white pr-4">{t('faq.q1')}</h3>
                  <span className="material-symbols-outlined text-emerald-500 transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="px-6 pb-6 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {t('faq.a1')}
                </div>
              </details>

              {/* FAQ Item 2 */}
              <details className="group glass-panel rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white pr-4">{t('faq.q2')}</h3>
                  <span className="material-symbols-outlined text-emerald-500 transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="px-6 pb-6 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {t('faq.a2')}
                </div>
              </details>

              {/* FAQ Item 3 */}
              <details className="group glass-panel rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white pr-4">{t('faq.q3')}</h3>
                  <span className="material-symbols-outlined text-emerald-500 transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="px-6 pb-6 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {t('faq.a3')}
                </div>
              </details>

              {/* FAQ Item 4 */}
              <details className="group glass-panel rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white pr-4">{t('faq.q4')}</h3>
                  <span className="material-symbols-outlined text-emerald-500 transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="px-6 pb-6 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {t('faq.a4')}
                </div>
              </details>

              {/* FAQ Item 5 */}
              <details className="group glass-panel rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white pr-4">{t('faq.q5')}</h3>
                  <span className="material-symbols-outlined text-emerald-500 transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="px-6 pb-6 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {t('faq.a5')}
                </div>
              </details>

              {/* FAQ Item 6 */}
              <details className="group glass-panel rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white pr-4">{t('faq.q6')}</h3>
                  <span className="material-symbols-outlined text-emerald-500 transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="px-6 pb-6 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {t('faq.a6')}
                </div>
              </details>
            </div>

            {/* Contact CTA */}
            <div className="mt-12 text-center">
              <p className="text-slate-600 dark:text-slate-400 mb-4">{t('faq.moreQuestions')}</p>
              <a
                href="https://calendar.app.google/vvEQCYQAiJemGrgr8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold hover:text-emerald-500 transition-colors"
              >
                {t('faq.contactUs')}
                <span className="material-symbols-outlined">arrow_forward</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Industry Compatibility Strip */}
      <div className="py-10 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900/60 border-t border-slate-200 dark:border-slate-800">
        <p className="text-center text-xs font-bold text-slate-400 uppercase tracking-widest mb-5">
          {i18n.language.startsWith('es') ? 'Funciona para cualquier negocio local' : 'Works for any local business'}
        </p>
        <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
          {[
            { icon: 'restaurant', label: i18n.language.startsWith('es') ? 'Restaurantes' : 'Restaurants' },
            { icon: 'content_cut', label: i18n.language.startsWith('es') ? 'Salones' : 'Salons & Spas' },
            { icon: 'handyman', label: i18n.language.startsWith('es') ? 'Servicios' : 'Service Businesses' },
            { icon: 'local_hospital', label: i18n.language.startsWith('es') ? 'Clínicas' : 'Clinics' },
            { icon: 'storefront', label: i18n.language.startsWith('es') ? 'Tiendas' : 'Retail Stores' },
            { icon: 'fitness_center', label: i18n.language.startsWith('es') ? 'Gimnasios' : 'Gyms' },
            { icon: 'cloud', label: 'SaaS' },
            { icon: 'more_horiz', label: i18n.language.startsWith('es') ? 'y más' : '& more' },
          ].map((badge) => (
            <span key={badge.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass-panel text-xs font-bold text-slate-600 dark:text-slate-300">
              <span className="material-symbols-outlined text-emerald-500 text-sm">{badge.icon}</span>
              {badge.label}
            </span>
          ))}
        </div>
      </div>

      {/* Footer */}
      <MegaFooter scrollToSection={scrollToSection} locale={i18n.language.startsWith('es') ? 'es' : 'en'} />

      {/* ── Scanning Animation Overlay ── */}
      {(scanState === 'scanning') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-10 max-w-sm w-full mx-4 text-center shadow-2xl border border-emerald-200/40">
            {/* Animated logo ring */}
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 rounded-full border-4 border-emerald-200 dark:border-emerald-900"></div>
              <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin"></div>
              <div className="absolute inset-2 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                <span className="material-symbols-outlined text-3xl text-emerald-600 dark:text-emerald-400 animate-pulse">auto_awesome</span>
              </div>
            </div>
            <h3 className="text-xl font-extrabold text-slate-900 dark:text-white mb-2">
              {i18n.language.startsWith('es') ? 'Analizando tu negocio...' : 'Analyzing your business...'}
            </h3>
            <div className="space-y-2 mt-6">
              {[
                t('scanAnimation.step1', { count: fakeReviewCount }),
                t('scanAnimation.step2'),
                t('scanAnimation.step3'),
              ].map((step, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-500 ${
                    scanStep > i
                      ? 'bg-emerald-50 dark:bg-emerald-900/30'
                      : scanStep === i
                      ? 'bg-slate-50 dark:bg-slate-800'
                      : 'opacity-30'
                  }`}
                >
                  {scanStep > i ? (
                    <span className="material-symbols-outlined text-emerald-500 text-lg flex-shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                  ) : scanStep === i ? (
                    <div className="w-4 h-4 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin flex-shrink-0"></div>
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600 flex-shrink-0"></div>
                  )}
                  <span className={`text-sm font-medium text-left ${scanStep >= i ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Lead Capture Modal ── */}
      {(scanState === 'modal' || scanState === 'success') && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-md p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setScanState('idle'); } }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full shadow-2xl border border-emerald-200/40 overflow-hidden animate-fade-in">
            {/* Header band */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-8 py-6">
              {scanState === 'success' ? (
                <div className="text-center">
                  <div className="text-4xl mb-2">🚀</div>
                  <h2 className="text-xl font-extrabold text-white">{t('leadModal.successTitle')}</h2>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {/* Mini social card previews */}
                    <div className="flex -space-x-1">
                      {['purple', 'blue', 'emerald'].map((c) => (
                        <div key={c} className={`w-8 h-8 rounded-lg bg-${c}-500/80 border-2 border-white flex items-center justify-center`}>
                          <span className="material-symbols-outlined text-white text-sm">image</span>
                        </div>
                      ))}
                    </div>
                    <span className="text-white/80 text-xs font-bold">3 cards ready</span>
                  </div>
                  <h2 className="text-xl font-extrabold text-white leading-tight">{t('leadModal.title')}</h2>
                </div>
              )}
            </div>

            <div className="px-8 py-7">
              {scanState === 'success' ? (
                <div className="text-center">
                  <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">{t('leadModal.successSubtitle')}</p>
                  <button
                    onClick={() => setScanState('idle')}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold py-3.5 rounded-xl transition-all"
                  >
                    {i18n.language.startsWith('es') ? 'Perfecto, ¡gracias!' : 'Got it, thanks!'}
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed text-sm">
                    {t('leadModal.subtitle', { businessName: selectedBusiness?.name ?? '' })}
                  </p>
                  <form onSubmit={handleLeadSubmit} className="space-y-3">
                    <input
                      type="text"
                      required
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      placeholder={t('leadModal.namePlaceholder')}
                      className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    />
                    <input
                      type="email"
                      required
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      placeholder={t('leadModal.emailPlaceholder')}
                      className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white font-extrabold py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/30 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {t('leadModal.sending')}
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-lg">send</span>
                          {t('leadModal.submitButton')}
                        </>
                      )}
                    </button>
                  </form>
                  <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-4">{t('leadModal.privacyNote')}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}